import React, { useState, useEffect } from 'react';
import { analyticsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import DateRangePicker from '../components/DateRangePicker';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  UserCheck,
  FileText,
  Calendar,
  Target,
  Award,
  Loader2,
  Eye,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  Shield,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Analytics = () => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  // Date scope: a `period` enum preset OR a custom { start_date, end_date } range.
  // Custom range takes precedence on the backend when both bounds are provided.
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState(null); // { from: Date, to: Date } or null
  const [rangeIso, setRangeIso] = useState(null); // { start_date: 'yyyy-MM-dd', end_date: 'yyyy-MM-dd' } or null
  const [teamSummary, setTeamSummary] = useState(null);
  const [recruitersData, setRecruitersData] = useState(null);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [recruiterDetails, setRecruiterDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  // Active sort column for the "All Recruiters Performance" table.
  // Joined is the default (matches the new ranking direction). Sticky
  // per-browser via localStorage so admins who track a specific metric
  // don't have to re-click every visit.
  const [sortBy, setSortBy] = useState(() => {
    try {
      const saved = localStorage.getItem('analytics_recruiter_sort');
      const valid = ['joined', 'offers', 'applications', 'candidates', 'conversion'];
      return valid.includes(saved) ? saved : 'joined';
    } catch {
      return 'joined';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('analytics_recruiter_sort', sortBy);
    } catch {
      // ignore
    }
  }, [sortBy]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, rangeIso]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, recruitersRes] = await Promise.all([
        analyticsApi.getTeamSummary(period, rangeIso),
        analyticsApi.getAllRecruitersPerformance(period, rangeIso)
      ]);
      setTeamSummary(summaryRes.data);
      setRecruitersData(recruitersRes.data);
    } catch (error) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const viewRecruiterDetails = async (recruiter) => {
    setSelectedRecruiter(recruiter);
    setIsDetailsOpen(true);
    setDetailsLoading(true);
    
    try {
      const response = await analyticsApi.getRecruiterDetails(recruiter.recruiter_id, period, rangeIso);
      setRecruiterDetails(response.data);
    } catch (error) {
      toast.error('Failed to load recruiter details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Rank by closures (joined) — the only outcome that actually moves revenue.
  // Tiebreakers (in order): offers made, applications created, candidates added.
  // We sort client-side so we don't change the shape of the shared backend
  // endpoints (consumed by other surfaces). The backend's original "by
  // candidates added" order is preserved in the raw response; we just
  // re-order locally before render.
  const sortByJoined = (a, b) => {
    const ja = a?.candidates_joined ?? 0;
    const jb = b?.candidates_joined ?? 0;
    if (jb !== ja) return jb - ja;
    const oa = a?.offers_made ?? 0;
    const ob = b?.offers_made ?? 0;
    if (ob !== oa) return ob - oa;
    const aa = a?.applications_created ?? 0;
    const ab = b?.applications_created ?? 0;
    if (ab !== aa) return ab - aa;
    return (b?.candidates_added ?? 0) - (a?.candidates_added ?? 0);
  };

  // Sort comparators for each column. Tiebreak by candidates_joined so the
  // "best closer" still wins ties — matters for sales and review calls.
  const SORT_COMPARATORS = {
    joined: sortByJoined,
    offers: (a, b) =>
      (b?.offers_made ?? 0) - (a?.offers_made ?? 0) ||
      (b?.candidates_joined ?? 0) - (a?.candidates_joined ?? 0),
    applications: (a, b) =>
      (b?.applications_created ?? 0) - (a?.applications_created ?? 0) ||
      (b?.candidates_joined ?? 0) - (a?.candidates_joined ?? 0),
    candidates: (a, b) =>
      (b?.candidates_added ?? 0) - (a?.candidates_added ?? 0) ||
      (b?.candidates_joined ?? 0) - (a?.candidates_joined ?? 0),
    conversion: (a, b) =>
      (b?.conversion_rate ?? 0) - (a?.conversion_rate ?? 0) ||
      (b?.candidates_joined ?? 0) - (a?.candidates_joined ?? 0),
  };

  const topPerformersByJoined = React.useMemo(() => {
    const arr = teamSummary?.top_performers || [];
    // Top Performers card is always "best closers" — not user-configurable
    // (it's a leaderboard, not a data table).
    return [...arr].sort(sortByJoined);
  }, [teamSummary]);

  const allRecruitersByJoined = React.useMemo(() => {
    const arr = recruitersData?.recruiters || [];
    const cmp = SORT_COMPARATORS[sortBy] || sortByJoined;
    return [...arr].sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruitersData, sortBy]);

  // Single source of truth for date scope changes from the picker
  const handleRangeChange = ({ preset, customRange: cr, start_date, end_date }) => {
    if (cr && start_date && end_date) {
      setCustomRange(cr);
      setRangeIso({ start_date, end_date });
      // Keep `period` reasonable for any code path that still reads it.
      setPeriod('custom');
    } else if (preset) {
      setPeriod(preset);
      setCustomRange(null);
      setRangeIso(null);
    }
  };

  const StatCard = ({ title, value, change, icon: Icon, color = 'teal' }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {change !== undefined && change !== null && (
              <div className={`flex items-center mt-1 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(change)}% vs last period</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-full bg-${color}-100 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getRankBadge = (index) => {
    if (index === 0) return <Badge className="bg-yellow-100 text-yellow-700">🥇 #1</Badge>;
    if (index === 1) return <Badge className="bg-slate-200 text-slate-700">🥈 #2</Badge>;
    if (index === 2) return <Badge className="bg-amber-100 text-amber-700">🥉 #3</Badge>;
    return <Badge variant="outline">#{index + 1}</Badge>;
  };

  const roleColors = {
    recruiter: 'bg-green-100 text-green-700',
    team_lead: 'bg-blue-100 text-blue-700',
  };

  // Check access
  if (!['admin', 'delivery_manager'].includes(currentUser?.role)) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only Admins and Delivery Managers can access analytics.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-teal-700" />
            Recruiter Performance Analytics
          </h1>
          <p className="text-slate-500 mt-1">Track team performance and identify top performers</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker
            preset={customRange ? null : period}
            customRange={customRange}
            onChange={handleRangeChange}
            testid="analytics-range"
          />
          <Button variant="outline" onClick={fetchData} data-testid="analytics-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Team Summary Cards */}
      {teamSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Candidates Added"
            value={teamSummary.current_period.candidates_added}
            change={teamSummary.changes.candidates_change}
            icon={Users}
            color="teal"
          />
          <StatCard
            title="Applications Created"
            value={teamSummary.current_period.applications_created}
            change={teamSummary.changes.applications_change}
            icon={FileText}
            color="blue"
          />
          <StatCard
            title="Interviews Scheduled"
            value={teamSummary.current_period.interviews_scheduled}
            change={teamSummary.changes.interviews_change}
            icon={Calendar}
            color="purple"
          />
          <StatCard
            title="Candidates Joined"
            value={teamSummary.current_period.candidates_joined}
            change={teamSummary.changes.joined_change}
            icon={UserCheck}
            color="green"
          />
        </div>
      )}

      {/* Pipeline Health */}
      {teamSummary?.pipeline_health && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Pipeline Health
            </CardTitle>
            <CardDescription>Current candidates in each stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(teamSummary.pipeline_health).map(([stage, count]) => (
                <div key={stage} className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-500 capitalize">{stage.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Performers */}
      {topPerformersByJoined.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-600" />
              Top Performers
            </CardTitle>
            <CardDescription>Recruiters with the most candidates <strong>joined</strong> this {period}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {topPerformersByJoined.slice(0, 5).map((performer, index) => (
                <div 
                  key={performer.recruiter_id}
                  className={`p-4 rounded-lg border-2 ${index === 0 ? 'border-yellow-300 bg-yellow-50' : 'border-slate-100 bg-slate-50'}`}
                  data-testid={`top-performer-${index}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    {getRankBadge(index)}
                  </div>
                  <p className="font-semibold text-slate-900 truncate">{performer.recruiter_name}</p>
                  <p className="text-sm text-slate-500 truncate">{performer.recruiter_email}</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Joined</span>
                      <span className="font-semibold text-emerald-700" data-testid={`top-performer-joined-${index}`}>
                        {performer.candidates_joined ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Offers</span>
                      <span className="font-medium">{performer.offers_made ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Candidates</span>
                      <span className="font-medium">{performer.candidates_added}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Conversion</span>
                      <span className="font-medium">{performer.conversion_rate}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Recruiters Table */}
      {recruitersData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              All Recruiters Performance
            </CardTitle>
            <CardDescription>
              {recruitersData.team_totals?.total_recruiters || 0} recruiters • 
              {recruitersData.team_totals?.total_candidates_added || 0} total candidates added · ranked by{' '}
              <span className="font-semibold text-slate-700">
                {sortBy === 'joined' ? 'closures' :
                 sortBy === 'offers' ? 'offers' :
                 sortBy === 'applications' ? 'applications' :
                 sortBy === 'conversion' ? 'conversion %' : 'candidates added'}
              </span>
              {' '}<span className="text-[11px] text-slate-400">(click any column header to re-sort)</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Role</TableHead>
                  {[
                    { key: 'candidates', label: 'Candidates' },
                    { key: 'applications', label: 'Applications' },
                    { key: '_interviews', label: 'Interviews' }, // not sortable
                    { key: 'offers', label: 'Offers' },
                    { key: 'joined', label: 'Joined' },
                    { key: 'conversion', label: 'Conversion' },
                  ].map((col) => {
                    const sortable = col.key !== '_interviews';
                    const active = sortable && sortBy === col.key;
                    return (
                      <TableHead
                        key={col.key}
                        className={`text-center ${sortable ? 'cursor-pointer select-none' : ''} ${
                          active ? 'text-emerald-700 font-semibold' : ''
                        }`}
                        onClick={sortable ? () => setSortBy(col.key) : undefined}
                        data-testid={sortable ? `analytics-sort-${col.key}` : undefined}
                        title={sortable ? `Sort by ${col.label}` : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {active ? <ArrowDownRight className="w-3 h-3" /> : null}
                        </span>
                      </TableHead>
                    );
                  })}
                  <TableHead className="text-center">Jobs</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allRecruitersByJoined.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      No recruiters found
                    </TableCell>
                  </TableRow>
                ) : (
                  allRecruitersByJoined.map((recruiter, index) => (
                    <TableRow key={recruiter.recruiter_id} data-testid={`recruiter-row-${index}`}>
                      <TableCell>{getRankBadge(index)}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{recruiter.recruiter_name}</p>
                          <p className="text-xs text-slate-500">{recruiter.recruiter_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleColors[recruiter.role] || 'bg-slate-100'}>
                          {recruiter.role === 'team_lead' ? 'Team Lead' : 'Recruiter'}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-center ${sortBy === 'candidates' ? 'bg-emerald-50/40 font-semibold' : 'font-medium'}`}>{recruiter.candidates_added}</TableCell>
                      <TableCell className={`text-center ${sortBy === 'applications' ? 'bg-emerald-50/40 font-semibold' : ''}`}>{recruiter.applications_created}</TableCell>
                      <TableCell className="text-center">{recruiter.interviews_scheduled}</TableCell>
                      <TableCell className={`text-center ${sortBy === 'offers' ? 'bg-emerald-50/40 font-semibold' : ''}`}>{recruiter.offers_made}</TableCell>
                      <TableCell className={`text-center ${sortBy === 'joined' ? 'bg-emerald-50/40' : ''}`} data-testid={`recruiter-joined-${index}`}>
                        <span className="text-emerald-700 font-bold">{recruiter.candidates_joined}</span>
                      </TableCell>
                      <TableCell className={`text-center ${sortBy === 'conversion' ? 'bg-emerald-50/40' : ''}`}>
                        <Badge variant="outline" className={
                          recruiter.conversion_rate >= 20 ? 'border-green-200 text-green-700' :
                          recruiter.conversion_rate >= 10 ? 'border-amber-200 text-amber-700' :
                          'border-slate-200 text-slate-600'
                        }>
                          {recruiter.conversion_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{recruiter.jobs_assigned}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewRecruiterDetails(recruiter)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recruiter Details Dialog */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              Recruiter Performance Details
            </DialogTitle>
            <DialogDescription>
              {selectedRecruiter?.recruiter_name} • {period === 'all' ? 'All Time' : `Last ${period}`}
            </DialogDescription>
          </DialogHeader>
          
          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : recruiterDetails ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-teal-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-700">{recruiterDetails.summary.candidates_added}</p>
                  <p className="text-sm text-teal-600">Candidates Added</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{recruiterDetails.summary.applications_created}</p>
                  <p className="text-sm text-blue-600">Applications</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-700">{recruiterDetails.summary.interviews_scheduled}</p>
                  <p className="text-sm text-purple-600">Interviews</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{recruiterDetails.summary.candidates_joined}</p>
                  <p className="text-sm text-green-600">Joined</p>
                </div>
              </div>

              {/* Stage Breakdown */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Application Stage Breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(recruiterDetails.stage_breakdown || {}).map(([stage, count]) => {
                    const total = recruiterDetails.summary.applications_created || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={stage} className="flex items-center gap-3">
                        <span className="w-32 text-sm text-slate-600 capitalize">{stage.replace('_', ' ')}</span>
                        <div className="flex-1">
                          <Progress value={percentage} className="h-2" />
                        </div>
                        <span className="w-16 text-sm font-medium text-right">{count} ({percentage}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top Skills */}
              {recruiterDetails.top_skills?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Top Skills Sourced</h4>
                  <div className="flex flex-wrap gap-2">
                    {recruiterDetails.top_skills.map((skill, i) => (
                      <Badge key={i} variant="outline" className="text-sm">
                        {skill.skill} ({skill.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Assigned Jobs */}
              {recruiterDetails.assigned_jobs?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Assigned Jobs ({recruiterDetails.assigned_jobs.length})</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {recruiterDetails.assigned_jobs.slice(0, 6).map((job) => (
                      <div key={job.id} className="p-3 bg-slate-50 rounded-lg flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{job.title}</p>
                          <p className="text-xs text-slate-500">{job.client_name}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{job.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {recruiterDetails.recent_activity?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Recent Activity</h4>
                  <ScrollArea className="h-48">
                    <div className="space-y-2">
                      {recruiterDetails.recent_activity.map((activity, i) => (
                        <div key={i} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                          <Activity className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm text-slate-700">{activity.action.replace('_', ' ')}</p>
                            <p className="text-xs text-slate-500">{activity.details}</p>
                          </div>
                          <span className="text-xs text-slate-400">
                            {format(new Date(activity.timestamp), 'MMM dd, HH:mm')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
